import { useState } from "react";
import { AlertCircle } from "lucide-react";

import LoadingSpinner from "@/components/LoadingSpinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Subscriber } from "@/types/subscription";
import { debugLogger } from "@/utils/debug";

import SubscriberRow from "./SubscriberRow";

interface SubscriberTableProps {
  data: Subscriber[] | undefined;
  loading: boolean;
  onSubscriptionChange: (id: string, updates: Partial<Subscriber>) => Promise<void>;
  onGrantAdminSubscription: (subscriber: Subscriber) => void;
  summary: string;
}

export default function SubscribersTable({
  data,
  loading,
  onSubscriptionChange,
  onGrantAdminSubscription,
  summary,
}: SubscriberTableProps) {
  const [processingSubscribers] = useState<Record<string, boolean>>({});

  debugLogger.log("SubscribersTable received data:", data?.length);

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="border-b border-gray-200 bg-gray-50 px-5 py-4">
        <h2 className="text-sm font-semibold text-gray-900">Subscriber list</h2>
        <p className="mt-1 text-sm text-gray-500">{summary}</p>
      </div>

      <Table>
        <TableHeader>
          <TableRow className="border-gray-200 hover:bg-transparent">
            <TableHead className="bg-white text-xs font-semibold uppercase tracking-[0.14em] text-gray-500">Subscriber</TableHead>
            <TableHead className="bg-white text-xs font-semibold uppercase tracking-[0.14em] text-gray-500">Status</TableHead>
            <TableHead className="bg-white text-xs font-semibold uppercase tracking-[0.14em] text-gray-500">Plan</TableHead>
            <TableHead className="bg-white text-xs font-semibold uppercase tracking-[0.14em] text-gray-500">Source</TableHead>
            <TableHead className="bg-white text-xs font-semibold uppercase tracking-[0.14em] text-gray-500">Access ends</TableHead>
            <TableHead className="bg-white text-right text-xs font-semibold uppercase tracking-[0.14em] text-gray-500">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={6} className="h-24 text-center">
                <div className="flex items-center justify-center">
                  <LoadingSpinner />
                </div>
              </TableCell>
            </TableRow>
          ) : !data || data.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} className="h-28 text-center">
                <div className="flex flex-col items-center justify-center gap-2">
                  <AlertCircle className="h-8 w-8 text-amber-500" />
                  <p className="text-sm font-medium text-gray-900">No subscribers match the current filters.</p>
                  <p className="text-sm text-gray-500">Try broadening the filter or search to bring more records into view.</p>
                </div>
              </TableCell>
            </TableRow>
          ) : (
            data.map((subscriber) => (
              <SubscriberRow
                key={subscriber.id}
                subscriber={subscriber}
                processingSubscribers={processingSubscribers}
                onSubscriptionChange={onSubscriptionChange}
                onGrantAdminSubscription={onGrantAdminSubscription}
              />
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}
